// ComposerResizable — Feb 19, 2026.
//
// Wraps the public Q&A chat textarea + send button + a custom drag handle
// that works on both desktop AND mobile. Replaces the native CSS `resize-y`
// handle (which is desktop-only and doesn't render on iOS/Android).
//
// Behavior contract (per Randy's spec):
//   • Default height is the smallest single-line size (44px). This makes
//     the composer unobtrusive on first render.
//   • Anyone (logged-in fan, anonymous visitor, owner) can drag the handle
//     to resize. Pointer Events API → one code path for mouse + touch.
//   • Only the OWNER's last height persists. Their drag end calls
//     PATCH /silos/{id} with composer_height_px so every future visitor
//     sees the owner-saved size.
//   • Non-owner drags are session-only. Refresh → back to the saved default.
//
// Isolation note: this file is the entire scope of iteration 1. Rolling it
// back means deleting this file, restoring the inline textarea block in
// PublicSilo.jsx, and dropping `composer_height_px` from the silo doc.
import { useEffect, useRef, useState } from "react";
import { PaperPlaneTilt, DotsSixVertical, Microphone, CircleNotch, SpeakerHigh, Waveform, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePublicPricing } from "@/lib/usePublicPricing";
import LLMGuardWrap from "@/components/LLMGuardWrap";

const MIN_PX = 44;          // hard floor — below this the placeholder gets cut off
const MAX_PX = 600;         // protect mobile viewport from being eaten
const FALLBACK_DEFAULT_PX = 54; // used if /public/pricing hasn't loaded yet

// Jun 2026 (Randy) — once a visitor has SEEN the "ask the creator to add voice"
// CTA on a silo, the send button stays a paper airplane (no waveform) for the
// rest of the session AND 24h, and we won't re-prompt them for a request.
const VOICE_CTA_KEY = (slug) => `mysilo_voice_cta_${slug}`;
const ctaSeen24h = (slug) => {
  if (!slug) return false;
  try {
    const t = Number(localStorage.getItem(VOICE_CTA_KEY(slug)) || 0);
    return !!t && (Date.now() - t) < 24 * 3600 * 1000;
  } catch { return false; }
};
const markCtaSeen = (slug) => {
  try { if (slug) localStorage.setItem(VOICE_CTA_KEY(slug), String(Date.now())); } catch { /* ignore */ }
};

export default function ComposerResizable({
  siloId,
  slug,
  siloOwnerHeightPx, // value from /api/s/:slug → silo.composer_height_px (may be null)
  viewerIsOwner,
  input,
  setInput,
  onKeyDown,
  onSend,
  loading,
  placeholder,
  // When the OWNER's Send button is blocked by the Zero-Balance guard (their
  // silo has no key + empty voucher), the parent supplies this so the locked
  // overlay opens a funding CTA instead of doing nothing.
  onSendBlocked,
  // Jun 2026 — Voice conversation. voiceEnabled = the silo has voice turned on
  // (the send button shows the waveform). voiceReady = it can actually run right
  // now (owner OpenAI key OR funded voucher). When ready, tapping the waveform
  // starts a voice chat; when NOT ready, it opens the "ask the creator" CTA.
  voiceEnabled = false,
  voiceReady = false,
  voiceState = "idle", // idle | recording | processing | speaking
  onVoicePress,
  onStopSpeaking,
}) {
  // Jun 2026 (Randy) — Voice activates by a single TAP on the waveform send
  // button (no more press-and-hold / double-click). The same button drives a
  // live conversation once started (tap = take over / end).
  const activeVoice = voiceState !== "idle";
  const activateVoice = () => { if (voiceEnabled && voiceState === "idle") onVoicePress?.(); };

  // CTA state for silos whose voice isn't set up yet.
  const [showCta, setShowCta] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [requestDone, setRequestDone] = useState(false);
  const [seenTick, setSeenTick] = useState(0); // bump to re-evaluate ctaSeen24h
  const ctaSeen = (!voiceReady && voiceEnabled) ? ctaSeen24h(slug) : false; // eslint-disable-line no-unused-vars
  void seenTick;
  // Jul 2026 (Randy) — the instant the visitor clicks INTO the chat box (caret
  // showing, even before typing), flip the voice/waveform icon to the send
  // paper-plane. Clicking away while still empty reverts to the voice icon.
  const [focused, setFocused] = useState(false);

  // The waveform (voice) icon shows only when: voice is on for this silo, the
  // chat box is EMPTY, we're idle, the box is NOT focused, and either voice is
  // ready OR the visitor hasn't already dismissed the CTA in the last 24h.
  // Otherwise → paper airplane.
  const showWaveform = voiceEnabled && voiceState === "idle" && !input.trim() && !focused && (voiceReady || !ctaSeen);

  const openCtaFlow = () => {
    setRequestDone(false);
    setShowCta(true);
  };
  const closeCta = () => {
    markCtaSeen(slug);          // seeing the CTA flips the button to airplane for 24h
    setSeenTick((t) => t + 1);
    setShowCta(false);
    setTimeout(() => setRequestDone(false), 250);
  };
  const sendVoiceRequest = async () => {
    if (!slug || requesting) return;
    setRequesting(true);
    try {
      await api.post(`/s/${slug}/request-voice`);
    } catch { /* non-fatal — still flips to "request in" */ }
    setRequesting(false);
    setRequestDone(true);
    markCtaSeen(slug);
    setSeenTick((t) => t + 1);
    toast.success("Your request is in — the creator's been told. 🎙️");
  };

  const handleSendClick = () => {
    if (activeVoice) { onVoicePress?.(); return; } // take over / end
    if (showWaveform) {
      if (voiceReady) { activateVoice(); return; }
      openCtaFlow(); // voice not set up yet → ask the creator
      return;
    }
    if (input.trim()) onSend();
  };
  // Feb 19, 2026 — global composer default (set by super_admin in BumpStudio).
  // Per-silo override always wins over this when present.
  const pricing = usePublicPricing();
  const globalDefault = Math.max(
    MIN_PX,
    Math.min(MAX_PX, Number(pricing.composer_default_height_px) || FALLBACK_DEFAULT_PX),
  );

  // Effective height: owner-saved override wins, else fall back to the
  // global default (from platform_thresholds, configured in BumpStudio).
  const initial = Math.max(
    MIN_PX,
    Math.min(MAX_PX, Number(siloOwnerHeightPx) || globalDefault),
  );
  const [height, setHeight] = useState(initial);

  // If the silo doc reloads with a fresh owner-saved value, sync. This
  // handles the case where the owner saves a height in one tab, then
  // a visitor in another tab gets a fresh /api/s/:slug response.
  useEffect(() => {
    const next = Math.max(MIN_PX, Math.min(MAX_PX, Number(siloOwnerHeightPx) || globalDefault));
    setHeight(next);
  }, [siloOwnerHeightPx, globalDefault]);

  // Drag state — tracked in refs so the pointermove handler doesn't
  // re-bind on every re-render.
  const dragStartY = useRef(0);
  const dragStartHeight = useRef(0);
  const dragging = useRef(false);

  // Save the owner's chosen height once they release the drag. Visitors
  // never hit this — guarded by viewerIsOwner.
  const persistOwnerHeight = async (finalHeightPx) => {
    if (!viewerIsOwner || !siloId) return;
    try {
      await api.patch(`/silos/${siloId}`, { composer_height_px: finalHeightPx });
    } catch {
      // Non-fatal — height already updated locally for the rest of the
      // session. Owner can retry by dragging again.
    }
  };

  const onPointerDown = (e) => {
    // Capture pointer so we keep getting move/up events even if the
    // pointer leaves the handle. Works for mouse + touch + pen uniformly.
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragStartY.current = e.clientY;
    dragStartHeight.current = height;
    dragging.current = true;
    // Prevent iOS Safari from accidentally selecting text while dragging.
    e.preventDefault();
  };

  const onPointerMove = (e) => {
    if (!dragging.current) return;
    const dy = e.clientY - dragStartY.current;
    const next = Math.max(MIN_PX, Math.min(MAX_PX, dragStartHeight.current + dy));
    setHeight(next);
  };

  const onPointerUp = (e) => {
    if (!dragging.current) return;
    dragging.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    // Owner-only persistence. Snapshot the height now in case state
    // updates haven't flushed yet.
    persistOwnerHeight(height);
  };

  return (
    <div className="relative" data-testid="composer-resizable">
      <textarea
        data-testid="silo-chat-input"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={(e) => {
          setFocused(true);
          // Jul 2026 (Randy) — caret sometimes landed OUTSIDE the field on
          // mobile. Force the insertion point to the end of the field's
          // text so the blinking cursor always sits INSIDE the chat box
          // when you tap to type.
          const el = e.target;
          try { const v = el.value || ""; el.setSelectionRange(v.length, v.length); } catch { /* */ }
          // Feb 24 2026 (Randy pt-composer-whitebox-fix) — removed the
          // 80ms `scrollIntoView({block:"center"})` follow-up. On iOS
          // Safari that manual centering scroll pushed the page past the
          // outer container's natural end, revealing the body background
          // (#FDFCF8) as a white block BELOW the composer once the
          // keyboard opened. iOS Safari's own default "scroll focused
          // input into view" behavior already handles bringing the caret
          // into view without over-scrolling — the extra scrollIntoView
          // was doing more harm than good. The caret-placement fix above
          // (setSelectionRange) is what actually fixed the original bug;
          // the scrollIntoView was defensive. If the caret-outside-field
          // bug ever returns, restore the setTimeout+scrollIntoView call
          // — but with block:"nearest" instead of "center" so it doesn't
          // over-scroll.
        }}
        onBlur={() => setFocused(false)}
        disabled={loading}
        placeholder={placeholder}
        style={{
          height: `${height}px`,
          color: "var(--tpl-text)",
        }}
        className="w-full shadow-lg border tpl-border rounded-3xl pl-6 pr-16 py-3 tpl-surface focus:outline-none focus:ring-1 leading-relaxed whitespace-pre-wrap break-words resize-none"
      />

      {/* Send button — unchanged from the inline version. Wrapped in
          LLMGuardWrap so the OWNER (visiting their own public silo) sees a
          grey-out + lock icon when their bucket is empty. For anonymous /
          non-owner visitors the wrap is disabled (no guard call fires). */}
      <LLMGuardWrap
        surface="public_ask"
        siloId={siloId}
        enabled={!!viewerIsOwner}
        onBlockedClick={onSendBlocked}
        className="absolute right-2 top-2"
      >
        <button
          type="button"
          data-testid={activeVoice ? "silo-voice-mic" : (showWaveform ? "silo-voice-waveform" : "silo-chat-send")}
          onClick={handleSendClick}
          disabled={loading || (!showWaveform && !activeVoice && !input.trim())}
          aria-label={activeVoice
            ? (voiceState === "speaking" ? "Tap to take over" : "Tap to end the voice conversation")
            : (showWaveform ? "Talk out loud — tap to start a voice conversation" : "Send")}
          title={showWaveform ? "Tap to talk out loud" : undefined}
          className={`relative w-11 h-11 rounded-full flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
            voiceState === "listening" ? "bg-red-500 text-white animate-pulse" : "tpl-accent-bg"
          }`}
        >
          {voiceState === "thinking" ? (
            <CircleNotch size={18} weight="bold" className="animate-spin" />
          ) : voiceState === "speaking" ? (
            <SpeakerHigh size={18} weight="fill" />
          ) : activeVoice ? (
            <Microphone size={18} weight="fill" />
          ) : showWaveform ? (
            <Waveform size={20} weight="bold" />
          ) : (
            <PaperPlaneTilt size={18} weight="fill" />
          )}
        </button>
      </LLMGuardWrap>

      {/* Custom drag handle — visible on both desktop AND mobile (native
          CSS resize handles don't render on iOS/Android). Pointer Events
          give us one handler for mouse + touch + pen. The handle sits in
          the bottom-right corner just inside the rounded-3xl boundary. */}
      <div
        role="separator"
        aria-label="Drag to resize chat input"
        aria-orientation="horizontal"
        data-testid="composer-resize-handle"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="absolute bottom-1 right-3 w-6 h-6 flex items-center justify-center cursor-ns-resize touch-none select-none rounded-full hover:bg-black/5 active:bg-black/10"
        style={{ color: "var(--tpl-muted, #6B6A68)" }}
        title={viewerIsOwner
          ? "Drag to resize — your size is saved for everyone."
          : "Drag to resize (size resets on refresh)"}
      >
        {/* Two stacked dots → visually reads as a grip, rotated 90° */}
        <DotsSixVertical size={14} weight="bold" style={{ transform: "rotate(90deg)" }} />
      </div>

      {/* Jun 2026 (Randy) — "voice isn't set up yet" CTA. Opens when a visitor
          taps the waveform on a silo whose owner hasn't added an OpenAI key /
          funded the voucher. They can ask the creator to add it; afterwards the
          button becomes a paper airplane for 24h (no re-prompting). */}
      {showCta && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 px-4"
          data-testid="voice-cta-overlay"
          onClick={closeCta}
        >
          <div
            className="modal-safe bg-white rounded-2xl shadow-2xl max-w-sm w-full p-5 relative"
            onClick={(e) => e.stopPropagation()}
            data-testid="voice-cta-modal"
          >
            <button
              onClick={closeCta}
              className="absolute top-3 right-3 text-[#6B6A68] hover:text-[#1C1B1A]"
              data-testid="voice-cta-close"
              aria-label="Close"
            >
              <X size={18} weight="bold" />
            </button>
            <div className="flex items-center gap-2 text-[#234A2E] mb-2">
              <Waveform size={22} weight="bold" />
              <span className="text-sm font-semibold">Talk out loud</span>
            </div>
            {requestDone ? (
              <p className="text-sm text-[#3A5240] leading-relaxed" data-testid="voice-cta-done">
                Your request is in — we've let the creator know people want voice here. 🎙️
                For now, just type your questions.
              </p>
            ) : (
              <>
                <p className="text-sm text-[#3A5240] leading-relaxed">
                  Type your questions for now. This creator hasn't set up the speech‑to‑speech
                  feature yet. Want to talk out loud? Let them know — a nudge helps.
                </p>
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={sendVoiceRequest}
                    disabled={requesting}
                    data-testid="voice-cta-request-btn"
                    className="flex-1 text-sm font-semibold px-3 py-2 rounded-lg bg-[#234A2E] text-white hover:bg-[#173420] disabled:opacity-50"
                  >
                    {requesting ? "Sending…" : "Send request to creator"}
                  </button>
                  <button
                    onClick={closeCta}
                    data-testid="voice-cta-not-now"
                    className="text-sm px-3 py-2 rounded-lg border hairline text-[#1C1B1A] hover:bg-[#F0F1EE]"
                  >
                    Not now
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
